/** @jsxRuntime classic */
import type { CSSProperties } from "react";
import { normalizeVisitNameInput, shouldKeepWalkName, type WalkNameSnapshot } from "./walkName";

/** Mount with the visit/identity key so drafts never cross walks or accounts. */
// Each app supplies its own React instance. Importing the mobile React runtime
// into the console bundles a second hook dispatcher and breaks the whole page.
export function createWalkNameEditor(
  // React's private ref types differ between the apps' installed type versions.
  // Only the JSX factory crosses that boundary; props and hook state stay typed.
  React: Pick<typeof import("react"), "useRef" | "useState"> & { createElement: (...args: any[]) => any },
) {
  const { useRef, useState } = React;
  return function WalkNameEditor({ snapshot, onSave, editable = true, fallback = "Untitled walk", style }: {
    snapshot: WalkNameSnapshot;
    onSave: (name: string) => Promise<WalkNameSnapshot>;
    editable?: boolean;
    fallback?: string;
    style?: CSSProperties;
  }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [saved, setSaved] = useState<WalkNameSnapshot | null>(null);
    const inFlight = useRef(false);
    const cancelled = useRef(false);
    const visible = saved && shouldKeepWalkName(saved, snapshot) ? saved : snapshot;
    async function save() {
      if (cancelled.current || inFlight.current) return;
      const name = normalizeVisitNameInput(draft);
      if (!name) { setError("Enter a walk name."); return; }
      if (name === visible.name) { setEditing(false); setError(""); return; }
      inFlight.current = true;
      setSaving(true); setError("");
      try {
        const result = await onSave(name);
        setSaved(result);
        setEditing(false);
      } catch {
        setError("Couldn’t save the walk name. Your edit is kept. Tap the title and leave the field or press Enter to retry.");
      } finally {
        inFlight.current = false;
        setSaving(false);
      }
    }
    return <div>
      {editing && editable ? <div>
        <input autoFocus aria-label="Walk name" aria-invalid={!!error} maxLength={120}
          value={draft} readOnly={saving} style={{ ...style, width: "100%", background: "transparent" }}
          onChange={event => setDraft(event.target.value)} onBlur={() => void save()}
          onKeyDown={event => {
            if (event.key === "Enter") { event.preventDefault(); void save(); }
            if (event.key === "Escape" && !inFlight.current) { cancelled.current = true; setEditing(false); setError(""); }
          }} />
        {saving ? <p role="status" aria-live="polite">Saving…</p> : null}
      </div> : <h1 aria-label={visible.name || fallback} style={style}>{editable ? <button type="button" aria-label="Rename walk" style={{ font: "inherit", color: "inherit", letterSpacing: "inherit", textAlign: "left", border: 0, padding: 0, background: "transparent", cursor: "text" }} onClick={() => {
        cancelled.current = false; setDraft(visible.name || ""); setError(""); setEditing(true);
      }}>{visible.name || fallback}</button> : visible.name || fallback}</h1>}
      {error ? <p role="alert">{error}</p> : null}
    </div>;
  };
}
